import type { Vfs } from '../types/vfs';
import type { ICepBridgeClient } from '@mimir/primitive/premiere/cepBridgeClientType';
import type { Downloader } from '../types/downloader';
import type { BackendClient } from '../client/backendClient';
import type { Lock } from '../async/lock';
import type { GrowingFileSession } from '../types/growingFileSession';
import type { DesktopMainProcessRequireType } from '../desktop/ipc';
import type { LimitedNodeApi } from '../nodejs/limitedNodeApi/nodeApi';

export type AdobeApp =
  'Premiere'
| 'AfterEffects'
| 'Photoshop'
| 'Audition'
| 'unknown';


export interface Platform {
  isWindows: boolean;
  isMac: boolean;

  // The path separator, i.e. "\" for Windows, "/" for other systems
  pathSep: string;
}


interface ApplicationContextCommon extends Platform {
  nodeApi: LimitedNodeApi | null;
  backendClient: BackendClient;

  // To make sure that multiple clients do not try to append to the same growing file
  // since that can lead to corruption.
  clientInstanceId: string,
}

interface NodeApplicationContextCommon extends ApplicationContextCommon {
  nodeApi: LimitedNodeApi;
  vfs: Vfs;

  // TODO: Consider calling it nleClient in both AdobeContext and ElectronContext,
  // since it is no longer specific for CEP.
  cepBridgeClient: ICepBridgeClient;

  downloader: Downloader;
}

/**
 * Indicates that the code is running in a context
 * that has an Adobe CEP panel. Note that it does not necessarily
 * mean Premiere since our Mimir panel can be used in other Adobe
 * applications.
 */
export interface AdobePanelContext extends NodeApplicationContextCommon {
  type: 'adobePanel';

  /**
   * null if we could not recognize the application.
   */
  app: AdobeApp | null;

  /**
   * This is the signature of the cep.fs.readdir function as it is
   * actually used in our code.
   *
   * TODO: we should not need this anymore since we have nodeApi. Look into removing this.
   */
  readdir: (dir: string) => { err?: any, data?: string[] } | undefined;
}

/**
 * Indicates that the code is running in Electron. For now,
 * it can only mean that we are in Mimir Desktop.
 */
export interface ElectronContext extends NodeApplicationContextCommon {
  type: 'electron';

  mainRequire: DesktopMainProcessRequireType;
  rendererRequire: (s: string) => any;

  dvrMode: boolean;


  /**
   * Mapping from file path to last segment in the file, so that we can continue from the next segment,
   * even if the continued download of the growing file stops for some reason (e.g. clip deleted from
   * DVR). This works only during the same Mimir Desktop session of course.
   */
  growingFileSessions: Record<string, GrowingFileSession | undefined>,

  /**
   * When we "finalize" before uploading a sequence, we do two things: traverse the sequence to build
   * sequenceDetails, and export the project snapshot. It can happen that in between the two steps, one
   * of the growing clips is updated from "continueGrowingFileDownload" to have a more recent last
   * segment. This results in an inconsistency between the sequenceDetails JSON and the project itself,
   * which can result in dvrExporter.py failing to replace with the correct clip during central conform.
   * In order to avoid this, we make use of this lock. We lock it from both those code paths so that it
   * acts as a mutex to prevent any updates from "continueGrowingFileDownload" while we are in "finalize"
   * of "onUploadSubmit".
   */
  sequenceUploadLock: Lock,

}


/**
 * We are in a web browser.
 */
export interface BrowserContext extends ApplicationContextCommon {
  type: 'browser';
  inIframe: boolean;
  prematureVideoFrameReuseBugWorkaround: boolean;
}

export type ApplicationContext = AdobePanelContext | ElectronContext | BrowserContext;


export function isInAdobeApp(ctx: ApplicationContext): ctx is AdobePanelContext {
  return ctx.type === 'adobePanel';
}


export function isInPremiere(ctx: ApplicationContext): ctx is AdobePanelContext {
  return ctx.type === 'adobePanel' && ctx.app === 'Premiere';
}


export function isInElectron(ctx: ApplicationContext): ctx is ElectronContext {
  return ctx.type === 'electron';
}


export function isInBrowser(ctx: ApplicationContext): ctx is BrowserContext {
  return ctx.type === 'browser';
}


export function isInIframe(ctx: ApplicationContext): ctx is BrowserContext {
  return ctx.type === 'browser' && ctx.inIframe;
}


export function getAdobeAppLogoName(ctx: ApplicationContext): string | undefined {
  if (!isInAdobeApp(ctx)) {
    return undefined;
  }

  switch (ctx.app) {
    case 'Premiere': return 'premiere-logo';
    case 'Photoshop': return 'ps-logo';
    case 'AfterEffects': return 'ae-logo';
    case 'Audition': return 'audition-logo';
    default: return undefined;
  }
}
